import {
  Button,
  Divider,
  Loader,
  OverflowSidebarContent,
  Sidebar,
  SidebarFooter,
  SidebarHeader,
  SocialLinkButton,
  TextInput,
} from 'components';
import SelectInputControlled from 'components/Inputs/SelectInputControlled/SelectInputControlledV3';
import {
  COMMUNITY_ROLES_OPTIONS,
  COMMUNITY_ROUTES,
  GROUP_ROLES,
  GROUP_ROLES_OPTIONS,
  ROUTES,
} from 'configs';
import { find } from 'lodash/fp';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Linkify from 'react-linkify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { UserPlaceholder } from 'static';
import { closeModal } from 'store/actionCreators/modal';
import { getMyId } from 'store/selectors/auth';
import {
  getCurrentCommunity,
  isChangeUserRoleLoading,
} from 'store/selectors/currentCommunity';
import { getChangeGroupUserRoleLoading } from 'store/selectors/currentGroup';
import { getCurrentGroup } from 'store/selectors/currentGroup';
import { getData } from 'store/selectors/modal';
import { selectIsChatsAvailable } from 'store/selectors/subscriptions';
import { EditUserRoleData } from 'types';
import { getProfileName } from 'utils';
import { checkAuthorizedToMakeAdmin } from 'utils/roles';

import {
  ContentWrapper,
  IconSendMessage,
  LinkAndUpdateBtnWrapper,
  MainBtnWrapper,
  SocialLinksWrapper,
  StyledDescriptionBlock,
  StyledDescriptionText,
  StyledDescriptionTitle,
  StyledImage,
  StyledLink,
  StyledMessageBlock,
  StyledSendMessageLabel,
} from '../ViewMember/styled';
import { FIELDS } from './form';
import { useEditUserRole } from './hooks';
import { StyledForm } from './styled';

const EditUserRole = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authId = useSelector(getMyId);
  const chatsEnabled = useSelector(selectIsChatsAvailable);
  const community = useSelector(getCurrentCommunity);
  const group = useSelector(getCurrentGroup);
  const changeCommunityRoleLoading = useSelector(isChangeUserRoleLoading);
  const changeGroupRoleLoading = useSelector(getChangeGroupUserRoleLoading);
  const modalData = useSelector(getData) as EditUserRoleData;

  const { user, currentUserInCommunity, currentUserInGroup, isForGroup } =
    modalData;

  const { profile } = user;
  const role = isForGroup
    ? user['groupPermission']?.role
    : user.communityPermission.role;

  const { onSubmit, errors, control } = useEditUserRole(
    isForGroup,
    profile.userId,
    community?.id,
    group?.id,
    role,
  );

  const isLoading = isForGroup
    ? changeGroupRoleLoading
    : changeCommunityRoleLoading;

  const isAuthorize = checkAuthorizedToMakeAdmin(
    currentUserInCommunity,
    currentUserInGroup,
  );

  const roleOptions = useMemo(() => {
    if (!isForGroup) {
      return COMMUNITY_ROLES_OPTIONS;
    }

    return isAuthorize
      ? GROUP_ROLES_OPTIONS
      : GROUP_ROLES_OPTIONS.filter((item) => item.value !== GROUP_ROLES.ADMIN);
  }, [isForGroup, isAuthorize]);

  const socialLinks =
    profile.links?.filter(
      (l) =>
        !!l.value &&
        (l.label === 'facebook' ||
          l.label === 'linkedin' ||
          l.label === 'instagram' ||
          l.label === 'twitter' ||
          l.label === 'youtube'),
    ) || [];

  const itsMe = authId === profile.userId;
  const showChatButton = chatsEnabled && !itsMe && !!community?.id;

  const communityRoleLabel = useMemo(() => {
    if (!isForGroup || !user.communityPermission?.role) {
      return '';
    }
    return (
      find({ value: user.communityPermission.role }, COMMUNITY_ROLES_OPTIONS)
        ?.label ?? user.communityPermission.role
    );
  }, [isForGroup, user.communityPermission?.role]);

  const getDOB = (dob: string) => {
    return moment(dob)
      .tz(profile.timeZone || 'Europe/Zurich')
      .format('YYYY-MM-DD');
  };

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleSendingMessage = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!community?.id) {
      return;
    }
    handleClose();
    navigate(
      `${ROUTES.COMMUNITY}/${community.id}/${COMMUNITY_ROUTES.CHAT}?peerId=${profile.userId}`,
    );
  };

  const noopOpenEdit = () => {};

  return (
    <Sidebar>
      <StyledForm onSubmit={onSubmit}>
        <SidebarHeader
          label={getProfileName(profile)}
          onCloseClick={handleClose}
        />
        <OverflowSidebarContent>
          <StyledImage
            src={profile.logo ? profile.logo : UserPlaceholder}
            alt=""
          />
          <ContentWrapper>
            {showChatButton && (
              <MainBtnWrapper>
                <Divider customMargin="0px" />
                <LinkAndUpdateBtnWrapper>
                  <StyledLink onClick={handleSendingMessage}>
                    <StyledMessageBlock>
                      <IconSendMessage />
                      <StyledSendMessageLabel>
                        {t('common.sendMessage')}
                      </StyledSendMessageLabel>
                    </StyledMessageBlock>
                  </StyledLink>
                </LinkAndUpdateBtnWrapper>
                <Divider customMargin="0px 0px 0px 0px" />
              </MainBtnWrapper>
            )}
            {socialLinks.length > 0 && (
              <>
                <SocialLinksWrapper>
                  {socialLinks.map((l, index) => (
                    <SocialLinkButton
                      key={`${l.label} + ${index}`}
                      openEditSidebar={noopOpenEdit}
                      href={l.value}
                      socialNetwork={l.label}
                      width="16px"
                    />
                  ))}
                </SocialLinksWrapper>
                <Divider customMargin="0px 0px 0px 0px" />
              </>
            )}
            {profile.description && (
              <StyledDescriptionBlock>
                <StyledDescriptionTitle>
                  {t('common.aboutMe')}
                </StyledDescriptionTitle>
                <Linkify
                  componentDecorator={(decoratedHref, decoratedText, key) => (
                    <a target="blank" href={decoratedHref} key={key}>
                      {decoratedText}
                    </a>
                  )}
                >
                  <StyledDescriptionText>
                    {profile.description}
                  </StyledDescriptionText>
                </Linkify>
              </StyledDescriptionBlock>
            )}
            <SelectInputControlled
              name={FIELDS.ROLE}
              control={control}
              width="100%"
              defaultValue={find(
                { value: role },
                isForGroup ? GROUP_ROLES_OPTIONS : COMMUNITY_ROLES_OPTIONS,
              )}
              options={roleOptions}
              label={
                isForGroup ? t('group.groupRole') : t('community.communityRole')
              }
              error={errors[FIELDS.ROLE]?.value}
            />
            {isForGroup && !!communityRoleLabel && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('community.communityRole')}
                value={communityRoleLabel}
              />
            )}
            {!!profile.profileName && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.profileName')}
                value={profile.profileName}
              />
            )}
            {!!profile.firstName && (
              <TextInput
                inputValueStyle={{ textTransform: 'capitalize' }}
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.firstName')}
                value={profile.firstName}
              />
            )}
            {!!profile.lastName && (
              <TextInput
                disabled
                inputValueStyle={{ textTransform: 'capitalize' }}
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.lastName')}
                value={profile.lastName}
              />
            )}
            {!!profile.email && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.email')}
                value={profile.email}
              />
            )}
            {!!profile.dob && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.dateOfBirth')}
                value={getDOB(profile.dob)}
              />
            )}
            {!!profile.freeText1 && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.freeText1') || 'Free Text 1'}
                value={profile.freeText1}
              />
            )}
            {!!profile.freeText2 && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.freeText2') || 'Free Text 2'}
                value={profile.freeText2}
              />
            )}
            {!!profile?.phones &&
              profile?.phones?.length > 0 &&
              profile?.phones.map((numberList, index) => (
                <TextInput
                  disabled
                  extraWrapperStyle={{ justifyContent: 'center' }}
                  key={`${numberList.label} + ${index}`}
                  label={t(`profile.${numberList.label}`)}
                  value={numberList.value}
                />
              ))}
            {!!profile.street && (
              <TextInput
                disabled
                inputValueStyle={{ textTransform: 'capitalize' }}
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.street')}
                value={profile.street}
              />
            )}
            {!!profile.region && (
              <TextInput
                disabled
                inputValueStyle={{ textTransform: 'capitalize' }}
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.region')}
                value={profile.region}
              />
            )}
            {!!profile.postalCode && (
              <TextInput
                disabled
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.postalCode')}
                value={profile.postalCode}
              />
            )}
            {!!profile.city && (
              <TextInput
                disabled
                inputValueStyle={{ textTransform: 'capitalize' }}
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.city')}
                value={profile.city}
              />
            )}
            {!!profile.country && (
              <TextInput
                disabled
                inputValueStyle={{ textTransform: 'capitalize' }}
                extraWrapperStyle={{ justifyContent: 'center' }}
                label={t('profile.country')}
                value={profile.country}
              />
            )}
          </ContentWrapper>
        </OverflowSidebarContent>
        <SidebarFooter>
          <Button
            type="submit"
            variant={isLoading ? 'secondary' : null}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader type="button" size="28px" thickness={2} />
            ) : (
              t('common.saveChanges')
            )}
          </Button>
        </SidebarFooter>
      </StyledForm>
    </Sidebar>
  );
};

export default EditUserRole;
